import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../lib/utils';

/**
 * Tooltip component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 202:622):
 * - Background: Grey-950 (#09090B)
 * - Text: White
 * - Border Radius: 8px
 * - Padding: 6px 10px
 * - Positions: Top, Bottom, Left, Right
 * - Arrow: 12x8px triangle
 */
const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef(
  ({ className, sideOffset = 4, ...props }, ref) => (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 overflow-hidden rounded-lg bg-grey-950 px-2.5 py-1.5 text-sm text-white shadow-md',
        'animate-in fade-in-0 zoom-in-95',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[side=bottom]:slide-in-from-top-2',
        'data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2',
        'data-[side=top]:slide-in-from-bottom-2',
        'dark:bg-grey-50 dark:text-grey-950',
        className
      )}
      {...props}
    />
  )
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * SimpleTooltip - Convenience wrapper for common tooltip usage
 *
 * Usage:
 *   <SimpleTooltip content="Helpful text">
 *     <Button>Hover me</Button>
 *   </SimpleTooltip>
 */
const SimpleTooltip = ({ children, content, side = 'top', delayDuration = 200, ...props }) => (
  <TooltipProvider delayDuration={delayDuration}>
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} {...props}>
        {content}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);
SimpleTooltip.displayName = 'SimpleTooltip';

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, SimpleTooltip };
